<template>
  <div v-if="canUpdate">
    <v-dialog
      v-if="device"
      v-model="showRebootDevice"
      max-width="480px"
    >
      <videon-card :heading="'Reboot ' + device.deviceName()" showClose="true" @close="showRebootDevice = false">
        <v-card-text>
          <p class="subtitle-1 my-5">
            Are you sure you want to reboot <span class="primary--text">{{ device.deviceName() }}</span>?
          </p>
          <v-form @submit.prevent="rebootDevice">
            <v-text-field
              v-model="rebootDeviceConfirm"
              label="Reboot Confirmation"
              messages="Please type CONFIRM above to proceed"
              required
              outlined
            ></v-text-field>
            <div class="mt-3 d-flex align-end">
              <v-spacer />
              <v-btn
                class="ma-1"
                color="secondary"
                @click="showRebootDevice = false"
                text
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1"
                color="primary"
                @click="rebootDevice"
              >
                Reboot
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <div v-if="!hasUpdates">
      <v-card
        color="info"
        outlined
        dark
        class="my-3"
      >
        <v-card-text>
          <h4 class="text-uppercase white--text d-inline-flex align-center"><v-icon color="white" class="mr-2">system_update_alt</v-icon> Firmware Update</h4>
          
          <device-update-state-rows :rows="updateStateDetailRows" row-key-prefix="ec-none" />
          
          <v-alert
            text
            outlined
            color="disabled"
            icon="mdi-alert-outline"
            class="mt-3 mb-0"
            v-if="updateStatus === 'complete'"
          >
            {{ updateMessage }}
          </v-alert>
          
          <v-progress-linear
            class="mt-5 mb-2"
            color="white"
            indeterminate
            buffer-value="0"
            v-if="isUpdating"
          ></v-progress-linear>
          
          <v-btn @click="updateAction('check_for_update')" color="secondary" width="100%" class="mt-4" :disabled="disableUpdateButton">Check for Update</v-btn>
        </v-card-text>
      </v-card>
    </div>
    <div v-if="hasUpdates">
      <v-card
        color="info"
        outlined
        dark
        class="my-3"
      >
        <v-card-text>
          <h4 class="text-uppercase white--text d-inline-flex align-center"><v-icon color="white" class="mr-2">system_update_alt</v-icon> Firmware Update Available</h4>
          
          <device-update-state-rows :rows="updateStateDetailRows" row-key-prefix="ec-yes" />
          
          <v-alert
            text
            outlined
            color="black"
            class="mt-3 mb-0"
            v-if="updateStatus === 'rejected' || updateStatus === 'error'"
          >
            {{ updateMessage }}. This may be due to slow download speeds. Check back later to apply update.
          </v-alert>
          
          <v-alert
            text
            outlined
            color="disabled"
            icon="mdi-alert-outline"
            class="mt-3 mb-0"
            v-else-if="updateStatus === 'complete'"
          >
            {{ updateMessage }}
          </v-alert>
          
          <v-alert
            text
            outlined
            color="disabled"
            icon="mdi-alert-outline"
            class="mt-3 mb-0"
            v-else-if="device && !device.isOnline() && !isUpdating"
          >
            {{ offlineUpdatesSummary }}. However, you are unable to apply updates while the device is offline.
          </v-alert>
          
          <p class="mb-0 mt-2">
            <a href="https://support.videonlabs.com/hc/en-us/articles/360016605353-Device-Software-and-Release-Notes" target="_blank">Device Software and Release Notes</a>
          </p>
          
          <div v-if="device && device.isOnline()">
            <v-progress-linear
              class="mt-5 mb-2"
              color="white"
              indeterminate
              buffer-value="0"
              v-if="isUpdating"
            ></v-progress-linear>
            <v-btn v-if="awaitingReboot" @click="showRebootDevice = true" color="red" width="100%" class="mt-4" :disabled="disableUpdateButton">{{ rebootButtonLabel }}</v-btn>
            <v-btn v-else @click="updateAction()" color="secondary" width="100%" class="mt-4" :disabled="disableUpdateButton">{{ primaryUpdateButtonLabel }}</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
  import DeviceUpdateStateRows from './DeviceUpdateStateRows.vue'
  import { runDeviceFirmwareCommand } from './deviceFirmwareCommand.js'
  
  export default {
    name: 'DeviceFirmwareUpdaterEdgecaster',
    components: {
      DeviceUpdateStateRows,
    },
    props: ['device'],
    
    data() {
      return {
        showRebootDevice: false,
        rebootDeviceConfirm: '',
      }
    },
    
    computed: {
      canUpdate() {
        if (this.device.state && this.device.state.update_state) {
          return true
        }
        return false
      },
      
      canEdit() {
        return this.device.canEdit()
      },
      
      // Prefer daemon over system when both have updates (single firmware card).
      pendingFirmwareUpdate() {
        return this.device.updatesAvailable['daemon'] || this.device.updatesAvailable['system']
      },
      
      version() {
        return this.pendingFirmwareUpdate
      },
      
      hasUpdates() {
        return !!this.pendingFirmwareUpdate
      },
      
      primaryUpdateButtonLabel() {
        var pd = this.device.updatesAvailable['daemon']
        var ps = this.device.updatesAvailable['system']
        var vo = this.version && this.version.original
        if (pd && ps) {
          return 'Download updates'
        }
        if (pd) {
          return vo ? ('Download daemon update (' + vo + ')') : 'Download daemon update'
        }
        if (ps) {
          return vo ? ('Download system ' + vo) : 'Download system update'
        }
        return 'Download update'
      },
      
      rebootButtonLabel() {
        var d = this.device.updatesAvailable['daemon']
        var s = this.device.updatesAvailable['system']
        if (d && s) {
          return 'Reboot to apply updates'
        }
        if (d) {
          return 'Reboot to apply daemon update'
        }
        return 'Reboot to apply system update'
      },
      
      offlineUpdatesSummary() {
        var pd = this.device.updatesAvailable['daemon']
        var ps = this.device.updatesAvailable['system']
        var vo = this.version && this.version.original
        if (pd && ps) {
          return 'Daemon and system updates are available'
        }
        if (vo) {
          return vo + ' is available'
        }
        return 'An update is available'
      },
      
      awaitingReboot() {
        if (!this.device.state || !this.device.state.update_state) {
          return false
        }
        var h = this.$helpers
        var us = this.device.state.update_state
        var daemonState = h.findUpdateStateEntry(us, 'daemon')
        if (daemonState && h.updateStateStatusNeedsRebootPrompt(daemonState.status)) {
          return true
        }
        var systemState = h.findUpdateStateEntry(us, 'system')
        return !!(systemState && h.updateStateStatusNeedsRebootPrompt(systemState.status))
      },
      
      disableUpdateButton() {
        if (!this.canEdit || !this.device.isOnline() || this.device.isUpdating()) {
          return true
        }
        return false
      },
      
      isUpdating() {
        return this.device.isUpdating()
      },
      
      updateStatus() {
        return (this.device.command === 'update_device') ? this.device.command_status : false
      },
      
      updateMessage() {
        return (this.device.command === 'update_device') ? this.device.command_message : ''
      },
      
      updateStateDetailRows() {
        if (!this.device.state || !this.device.state.update_state) {
          return []
        }
        var h = this.$helpers
        var us = this.device.state.update_state
        var rows = h.updateStateRowsNotUpToDate(us).slice()
        var isUpToDateEquivalent = function (status) {
          var s = h.normalizeUpdateStateStatus(status)
          return s === 'up to date' || s === 'already on newer version' || s === 'already on newest version'
        }
        var hasModuleRow = function (moduleName) {
          var m = String(moduleName).toLowerCase()
          return rows.some(function (r) {
            return r && r.module != null && String(r.module).toLowerCase() === m
          })
        }
        
        // Always show daemon/system state when present so both modules are visible.
        var daemon = h.findUpdateStateEntry(us, 'daemon')
        if (daemon && daemon.status != null && String(daemon.status).trim() !== '' && !isUpToDateEquivalent(daemon.status) && !hasModuleRow('daemon')) {
          rows.unshift(daemon)
        }
        
        var system = h.findUpdateStateEntry(us, 'system')
        if (system && system.status != null && String(system.status).trim() !== '' && !isUpToDateEquivalent(system.status) && !hasModuleRow('system')) {
          rows.unshift(system)
        }
        
        return rows
      },
    },
    methods: {
      rebootDevice() {
        if (this.rebootDeviceConfirm == 'CONFIRM') {
          this.device.runCommand('reboot_device')
          this.showRebootDevice = false
          this.$emit('close')
        }
      },
      
      updateAction(command = 'download_update') {
        runDeviceFirmwareCommand(this, command).catch(function () {
          /* error state set on device */
        })
      }
    }
  }
</script>